import {
  Component,
  signal,
  computed,
  inject,
  input,
  type OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { PIcon } from '@primeicons/angular/p-icon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import type { NavGroup } from '../side-bar/interfaces/side-bar.interface';
import { Search } from '@primeicons/angular/search';
import type { FlatNavItem } from './interfaces/command-menu.interfaces';
import { BrowserStorageService } from '@shared/services/browser-storage.service';

@Component({
  selector: 'app-command-menu',
  imports: [
    FormsModule,
    DialogModule,
    ButtonModule,
    PIcon,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    Search,
  ],
  templateUrl: './command-menu.html',
  styleUrl: './command-menu.css',
  host: {
    '(window:keydown.control.k)': 'onKeydownShortcut($event)',
    '(window:keydown.meta.k)': 'onKeydownShortcut($event)',
    '(window:keydown.escape)': 'onEscapeKey()',
  },
})
export class CommandMenu implements OnInit {
  public readonly navGroups = input<NavGroup[]>([]);

  public visible = false;
  public searchQuery = signal('');
  public selectedIndex = signal(0);
  public recentItems = signal<FlatNavItem[]>([]);
  public isMobile = signal(false);
  private readonly browserStorageService = inject(BrowserStorageService);
  private router = inject(Router);
  private readonly STORAGE_KEY = 'ccl_recent_modules';

  public constructor() {
    this.setupMobileListener();
  }

  public ngOnInit(): void {
    this.loadRecentItems();
  }

  private setupMobileListener(): void {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia('(max-width: 1023px)');
    this.isMobile.set(mql.matches);
    mql.addEventListener('change', (e) => {
      this.isMobile.set(e.matches);
    });
  }

  private loadRecentItems(): void {
    const stored = sessionStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      this.recentItems.set(JSON.parse(stored));
    }
  }

  private saveRecentItem(item: FlatNavItem): void {
    const current = this.recentItems();
    const filtered = current.filter((i) => i.routerLink !== item.routerLink);
    filtered.unshift(item);
    if (filtered.length > 5) {
      filtered.pop();
    }
    this.recentItems.set(filtered);
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
  }

  public onKeydownShortcut(event: Event): void {
    event.preventDefault();
    this.showDialog();
  }

  public onEscapeKey(): void {
    if (this.visible) {
      this.hideDialog();
    }
  }

  public showDialog(): void {
    this.visible = true;
    this.searchQuery.set('');
    this.selectedIndex.set(0);
  }

  public hideDialog(): void {
    this.visible = false;
  }

  public flatItems = computed<FlatNavItem[]>(() => {
    const items: FlatNavItem[] = [];
    const currentGroups = this.navGroups();
    for (const group of currentGroups) {
      for (const item of group.items) {
        if (item.routerLink) {
          items.push({
            label: item.label,
            routerLink: item.routerLink,
            icon: item.icon,
            groupLabel: group.items[0]?.label,
          });
        }
        if (item.subItems) {
          for (const sub of item.subItems) {
            if (sub.routerLink) {
              items.push({
                label: sub.label,
                routerLink: sub.routerLink,
                icon: item.icon,
                groupLabel: item.label,
              });
            }
          }
        }
      }
    }
    return items;
  });

  public displayItems = computed(() => {
    const rawQuery = this.searchQuery();
    if (!rawQuery) {
      return this.recentItems();
    }

    // Normalize string to ignore accents
    const normalize = (str: string): string =>
      str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

    const query = normalize(rawQuery);
    return this.flatItems().filter((item) => {
      const label = normalize(item.label);
      const groupLabel = item.groupLabel ? normalize(item.groupLabel) : '';
      return label.includes(query) || groupLabel.includes(query);
    });
  });

  public onSearchChange(): void {
    this.selectedIndex.set(0);
  }

  public onArrowDown(event: Event): void {
    event.preventDefault();
    const items = this.displayItems();
    if (items.length > 0) {
      this.selectedIndex.update((i) => (i < items.length - 1 ? i + 1 : 0));
      this.scrollToSelected();
    }
  }

  public onArrowUp(event: Event): void {
    event.preventDefault();
    const items = this.displayItems();
    if (items.length > 0) {
      this.selectedIndex.update((i) => (i > 0 ? i - 1 : items.length - 1));
      this.scrollToSelected();
    }
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const el = document.getElementById('cmd-item-' + this.selectedIndex());
      if (el) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  public navigate(item: FlatNavItem): void {
    if (item.routerLink) {
      /* Borra todas las keys del almacenamiento local antes de navegar */
      this.browserStorageService.clearScope();

      this.saveRecentItem(item);
      this.router.navigate([item.routerLink]);
      this.hideDialog();
    }
  }

  public onEnter(): void {
    const items = this.displayItems();
    if (items.length > 0 && this.selectedIndex() >= 0 && this.selectedIndex() < items.length) {
      this.navigate(items[this.selectedIndex()]);
    }
  }
}
